import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteReview } from "../../apis/tagging";
import { removeReviewFromVenueCache } from "../business/details/venueReviewsCache";

export function useDeleteReview() {
  const queryClient = useQueryClient();
  const {
    data: deleteReviewData,
    error: deleteReviewError,
    mutate: deleteReviewMutation,
    status: deleteReviewStatus,
  } = useMutation({
    mutationFn: ({
      reviewId,
      accessToken,
    }: {
      reviewId: string;
      accessToken: string;
    }) => {
      return deleteReview(reviewId, accessToken).then((response) => {
        // `delete-review` answers a rejected delete (not the owner, wrong venue
        // template) with HTTP 200 and `{status: "error"}`, so without this the
        // mutation resolves and callers toast the failure as a success.
        if (!response || response.status === "error") {
          throw new Error(response?.message ?? "Could not delete this review.");
        }
        return response;
      });
    },
    onSuccess: async (_, { reviewId }) => {
      await queryClient.invalidateQueries({
        queryKey: ["loadAllCategories"],
        exact: false,
      });

      // Drop the row from the cached venue reviews up front. The delete is soft and the
      // venue cache is rebuilt asynchronously by `ReviewSaved`, so refetching now would
      // hand back the list still containing this review (a no-op for callers that don't
      // show venue reviews — the review simply isn't in any cached list).
      removeReviewFromVenueCache(queryClient, Number(reviewId));

      // Marked stale but deliberately not refetched: the rebuild runs on a queue with
      // no bounded delay, so a refetch we schedule ourselves returns the list still
      // containing this review and puts the deleted card straight back. The next mount
      // or refocus picks up the server's copy.
      queryClient.invalidateQueries({
        queryKey: ["loadVenueDetailsBySlug"],
        exact: false,
        refetchType: "none",
      });
    },
  });
  return {
    deleteReviewMutation,
    deleteReviewData,
    deleteReviewError,
    deleteReviewStatus,
  };
}
